import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Wrench,
  Search,
  Filter,
  Calendar as CalendarIcon,
  Eye,
  Pencil,
  Trash2,
  Printer,
  Loader2,
  Building2,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ImageIcon,
  Phone,
  User,
  X,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  MapPin,
  Camera,
  Upload,
  Send,
  Receipt,
  FileCheck,
  CircleDollarSign,
  ArrowLeftRight,
  ShieldCheck,
  ShieldAlert
} from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "../lib/imageCompressor";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const MaintenanceRequests = () => {
  const { user, token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timelineComment, setTimelineComment] = useState("");
  const [timelineImages, setTimelineImages] = useState([]);
  const [timelineType, setTimelineType] = useState("comment");
  const [submittingTimeline, setSubmittingTimeline] = useState(false);
  const [uploadingTimelineImg, setUploadingTimelineImg] = useState(false);
  const [requestingCompletion, setRequestingCompletion] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [expenseData, setExpenseData] = useState(null);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseDiagnosis, setExpenseDiagnosis] = useState("");
  const [expenseEstimatedCost, setExpenseEstimatedCost] = useState("");
  const [expenseApprovalNotes, setExpenseApprovalNotes] = useState("");
  const [expenseInvoiceImage, setExpenseInvoiceImage] = useState("");
  const [expenseActualAmount, setExpenseActualAmount] = useState("");
  const [expenseInvoiceNotes, setExpenseInvoiceNotes] = useState("");
  const [submittingExpense, setSubmittingExpense] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [showDirectExpenseForm, setShowDirectExpenseForm] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: "",
    scheduled_date: null,
    assigned_to: "",
    assigned_to_user_id: "",
    notes: ""
  });

  useEffect(() => {
    fetchRequests();
    fetchTechnicians();
  }, [token]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchQuery, statusFilter, priorityFilter, monthFilter, typeFilter]);

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTechnicians(response.data);
    } catch (error) {
      // Silently fail - technicians list is optional
      console.log("Could not fetch technicians");
    }
  };

  const handleTimelineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingTimelineImg(true);
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append("file", compressed);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setTimelineImages(prev => [...prev, res.data.data]);
    } catch {
      toast.error("فشل في رفع الصورة");
    } finally {
      setUploadingTimelineImg(false);
    }
  };

  const submitTimelineEntry = async () => {
    if (!timelineComment && timelineImages.length === 0) {
      toast.error("يرجى إضافة تعليق أو صورة");
      return;
    }
    setSubmittingTimeline(true);
    try {
      await axios.post(`${API}/maintenance/${selectedRequest.id}/timeline`, {
        comment: timelineComment,
        images: timelineImages,
        entry_type: timelineType
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تم إضافة التحديث بنجاح");
      setTimelineComment("");
      setTimelineImages([]);
      setTimelineType("comment");
      const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
      setRequests(res.data);
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch {
      toast.error("فشل في إضافة التحديث");
    } finally {
      setSubmittingTimeline(false);
    }
  };

  const fetchExpense = async (requestId) => {
    try {
      const res = await axios.get(`${API}/maintenance/${requestId}/expense`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenseData(res.data);
    } catch {
      setExpenseData(null);
    }
  };

  const submitDiagnosis = async () => {
    if (!expenseDiagnosis || !expenseEstimatedCost) {
      toast.error("يرجى إدخال التشخيص والتكلفة التقديرية");
      return;
    }
    setSubmittingExpense(true);
    try {
      await axios.post(`${API}/maintenance/${selectedRequest.id}/expense/diagnosis`, {
        diagnosis: expenseDiagnosis,
        estimated_cost: parseFloat(expenseEstimatedCost)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تم إرسال التشخيص بنجاح");
      setShowExpenseForm(false);
      setExpenseDiagnosis("");
      setExpenseEstimatedCost("");
      fetchExpense(selectedRequest.id);
      fetchRequests();
      const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في إرسال التشخيص");
    } finally {
      setSubmittingExpense(false);
    }
  };

  const handleExpenseApproval = async (approved) => {
    setSubmittingExpense(true);
    try {
      await axios.put(`${API}/maintenance/${selectedRequest.id}/expense/approve`, {
        approved,
        notes: expenseApprovalNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(approved ? "تمت الموافقة على المشتريات" : "تم رفض طلب المشتريات");
      setExpenseApprovalNotes("");
      fetchExpense(selectedRequest.id);
      fetchRequests();
      const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في تحديث الموافقة");
    } finally {
      setSubmittingExpense(false);
    }
  };

  const handleInvoiceImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append("file", compressed);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setExpenseInvoiceImage(res.data.data);
      toast.success("تم رفع صورة الفاتورة");
    } catch {
      toast.error("فشل في رفع الصورة");
    } finally {
      setUploadingInvoice(false);
    }
  };

  const submitInvoice = async () => {
    if (!expenseInvoiceImage || !expenseActualAmount) {
      toast.error("يرجى رفع صورة الفاتورة وإدخال المبلغ الفعلي");
      return;
    }
    setSubmittingExpense(true);
    try {
      await axios.post(`${API}/maintenance/${selectedRequest.id}/expense/invoice`, {
        invoice_image: expenseInvoiceImage,
        actual_amount: parseFloat(expenseActualAmount),
        notes: expenseInvoiceNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تم رفع الفاتورة بنجاح");
      setExpenseInvoiceImage("");
      setExpenseActualAmount("");
      setExpenseInvoiceNotes("");
      fetchExpense(selectedRequest.id);
      fetchRequests();
      const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في رفع الفاتورة");
    } finally {
      setSubmittingExpense(false);
    }
  };

  const submitDirectExpense = async () => {
    if (!expenseDiagnosis || !expenseEstimatedCost || !expenseInvoiceImage || !expenseActualAmount) {
      toast.error("يرجى تعبئة جميع الحقول المطلوبة");
      return;
    }
    setSubmittingExpense(true);
    try {
      await axios.post(`${API}/maintenance/${selectedRequest.id}/expense/direct`, {
        diagnosis: expenseDiagnosis,
        estimated_cost: parseFloat(expenseEstimatedCost),
        invoice_image: expenseInvoiceImage,
        actual_amount: parseFloat(expenseActualAmount),
        notes: expenseInvoiceNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تم إضافة المشتريات والفاتورة بنجاح");
      setShowDirectExpenseForm(false);
      setExpenseDiagnosis("");
      setExpenseEstimatedCost("");
      setExpenseInvoiceImage("");
      setExpenseActualAmount("");
      setExpenseInvoiceNotes("");
      fetchExpense(selectedRequest.id);
      fetchRequests();
      const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في إضافة المشتريات");
    } finally {
      setSubmittingExpense(false);
    }
  };

  const requestCompletion = async () => {
    if (!selectedRequest) return;
    setRequestingCompletion(true);
    try {
      await axios.post(`${API}/maintenance/${selectedRequest.id}/request-completion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم إرسال طلب الإكمال للمدير");
      const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
      setRequests(res.data);
      const updated = res.data.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في إرسال طلب الإكمال");
    } finally {
      setRequestingCompletion(false);
    }
  };

  const handleApproveCompletion = async (requestId, approved) => {
    try {
      await axios.post(`${API}/maintenance/${requestId}/approve-completion?approved=${approved}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(approved ? "تم تأكيد إكمال الطلب" : "تم رفض طلب الإكمال");
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        const res = await axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } });
        const updated = res.data.find(r => r.id === requestId);
        if (updated) setSelectedRequest(updated);
        else setDetailDialogOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في تحديث الطلب");
    }
  };

  const handleRedirectRequest = async (requestId, targetRole) => {
    setRedirecting(true);
    try {
      await axios.post(`${API}/maintenance/${requestId}/redirect?target_role=${targetRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const roleLabels = { maintenance_manager: "مدير الصيانة", it_manager: "مدير تقنية المعلومات", medical_manager: "المدير الطبي" };
      toast.success(`تم تحويل الطلب إلى ${roleLabels[targetRole]}`);
      fetchRequests();
      const res = await axios.get(`${API}/maintenance/${requestId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedRequest(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل في تحويل الطلب");
    } finally {
      setRedirecting(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Maintenance fetch error:", error?.response?.status, error?.response?.data);
      toast.error("فشل في تحميل طلبات الصيانة");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Warranty data for dental chairs
  const getWarrantyInfo = (clinicName) => {
    if (!clinicName) return null;
    const match = clinicName.match(/(\d+)/);
    if (!match) return null;
    const num = parseInt(match[1]);

    const group1 = [1,2,3,4,5,6,7,8,9,10,11,12,13,15,16,17,19];
    const group2 = [14,20,21,29,30];
    const group3 = [18];
    const group4 = [22,23,24,25,26,27,28];

    let purchaseDate, warrantyYears;
    if (group1.includes(num)) {
      purchaseDate = new Date(2022, 11, 29);
      warrantyYears = 5;
    } else if (group2.includes(num)) {
      purchaseDate = new Date(2025, 8, 25);
      warrantyYears = 5;
    } else if (group3.includes(num)) {
      purchaseDate = new Date(2025, 8, 25);
      warrantyYears = 3;
    } else if (group4.includes(num)) {
      purchaseDate = new Date(2025, 9, 9);
      warrantyYears = 5;
    } else {
      return null;
    }

    const expiryDate = new Date(purchaseDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + warrantyYears);
    const now = new Date();
    const isActive = now <= expiryDate;
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    return { purchaseDate, expiryDate, warrantyYears, isActive, daysLeft, clinicNum: num };
  };

  const filterRequests = () => {
    let filtered = [...requests];
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(req =>
        [req.clinic_name, req.location_name, req.description, req.category, req.reporter_name, req.element_name]
          .some(v => (v || "").toString().toLowerCase().includes(q))
      );
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    if (priorityFilter !== "all") {
      filtered = filtered.filter(req => req.priority === priorityFilter);
    }

    if (monthFilter !== "all") {
      filtered = filtered.filter(req => (req.created_at || "").slice(0, 7) === monthFilter);
    }

    if (typeFilter !== "all") {
      if (typeFilter === "medical") {
        filtered = filtered.filter(req => req.element_type === "medical" || req.element_type === "device");
      } else {
        filtered = filtered.filter(req => req.element_type === typeFilter);
      }
    }
    
    setFilteredRequests(filtered);
  };

  const monthOptions = Array.from(new Set(requests.map(r => (r.created_at || "").slice(0, 7)).filter(Boolean))).sort().reverse();
  const formatMonthLabel = (ym) => {
    const names = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
    const [y, m] = ym.split("-");
    return `${names[parseInt(m, 10) - 1]} ${y}`;
  };

  const handleUpdate = async () => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      const data = {};
      if (updateData.status) data.status = updateData.status;
      if (updateData.scheduled_date) {
        data.scheduled_date = updateData.scheduled_date.toISOString();
        // تغيير الحالة تلقائياً إلى "مجدول" عند تعيين تاريخ جدولة
        // إلا إذا كانت الحالة المختارة "قيد التنفيذ" أو "مكتمل"
        if (updateData.status !== "in_progress" && updateData.status !== "completed" && updateData.status !== "cancelled") {
          data.status = "scheduled";
        }
      }
      if (updateData.assigned_to) data.assigned_to = updateData.assigned_to;
      if (updateData.assigned_to_user_id) data.assigned_to_user_id = updateData.assigned_to_user_id;
      if (updateData.notes !== undefined) data.notes = updateData.notes;

      await axios.put(`${API}/maintenance/${selectedRequest.id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم تحديث الطلب بنجاح");
      fetchRequests();
      setDialogOpen(false);
    } catch (error) {
      toast.error("فشل في تحديث الطلب");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الطلب؟")) return;
    
    try {
      await axios.delete(`${API}/maintenance/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف الطلب بنجاح");
      fetchRequests();
    } catch (error) {
      toast.error("فشل في حذف الطلب");
    }
  };

  const openEditDialog = (request) => {
    setSelectedRequest(request);
    setUpdateData({
      status: request.status,
      scheduled_date: request.scheduled_date ? new Date(request.scheduled_date) : null,
      assigned_to: request.assigned_to || "",
      assigned_to_user_id: request.assigned_to_user_id || "",
      notes: request.notes || ""
    });
    setDialogOpen(true);
  };

  const openDetailDialog = async (request) => {
    setSelectedRequest(request);
    setCurrentImageIndex(0);
    setExpenseData(null);
    setShowExpenseForm(false);
    setExpenseDiagnosis("");
    setExpenseEstimatedCost("");
    setExpenseApprovalNotes("");
    setExpenseInvoiceImage("");
    setExpenseActualAmount("");
    setExpenseInvoiceNotes("");
    setShowDirectExpenseForm(false);
    setDetailDialogOpen(true);
    fetchExpense(request.id);
    // Fetch full request details with images
    try {
      const res = await axios.get(`${API}/maintenance/${request.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRequest(res.data);
    } catch (e) {
      console.error("Failed to fetch full request details", e);
    }
  };

  const handleQuickStatusUpdate = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/maintenance/${requestId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === "completed" ? "تم إكمال الطلب بنجاح" : "تم تحديث الحالة بنجاح");
      fetchRequests();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error("فشل في تحديث الحالة");
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "قيد الانتظار",
      scheduled: "مجدول",
      in_progress: "قيد التنفيذ",
      completed: "مكتمل",
      cancelled: "ملغي"
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = { low: "منخفضة", medium: "متوسطة", high: "عالية", urgent: "عاجلة" };
    return labels[priority] || priority;
  };

  const getElementTypeLabel = (type) => {
    const labels = {
      structural: "إنشائي",
      medical: "طبي",
      device: "جهاز طبي",
      it: "تقنية المعلومات"
    };
    return labels[type] || type;
  };

  const getElementTypeColor = (type) => {
    const colors = {
      structural: "bg-orange-100 text-orange-700",
      medical: "bg-red-100 text-red-700",
      device: "bg-red-100 text-red-700",
      it: "bg-cyan-100 text-cyan-700"
    };
    return colors[type] || "bg-slate-100 text-slate-700";
  };

  const getCategoryLabel = (category, request = null) => {
    if (request?.device_name) {
      return request.biomed_no ? `${request.device_name} (${request.biomed_no})` : request.device_name;
    }
    if (category && category.startsWith("biomed:")) return "جهاز طبي";
    const labels = {
      // إنشائي
      wall: "الجدران", 
      floor: "الأرضيات", 
      ceiling: "السقف", 
      lighting: "الإضاءة",
      plumbing: "السباكة", 
      electrical: "الكهرباء", 
      ac: "التكييف",
      doors_windows: "الأبواب والنوافذ",
      painting: "الدهانات",
      // طبي
      dental_chair: "كرسي الأسنان",
      xray: "جهاز الأشعة", 
      sterilizer: "جهاز التعقيم", 
      compressor: "الضاغط",
      suction: "جهاز الشفط", 
      light: "إضاءة الكرسي",
      medical_equipment: "أجهزة طبية أخرى",
      it_equipment: "جهاز إلكتروني",
      // تقنية المعلومات
      computer: "جهاز كمبيوتر",
      printer: "طابعة",
      network: "الشبكة والإنترنت",
      phone_system: "نظام الهاتف",
      software: "برامج وأنظمة",
      security_camera: "كاميرات المراقبة",
      access_control: "أنظمة الدخول",
      other: "أخرى"
    };
    return labels[category] || category;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4" />;
      case "scheduled": return <CalendarIcon className="w-4 h-4" />;
      case "in_progress": return <Wrench className="w-4 h-4" />;
      case "completed": return <CheckCircle2 className="w-4 h-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">طلبات الصيانة</h1>
        <p className="text-slate-500 mt-1">إدارة ومتابعة طلبات الصيانة ({requests.length} طلب)</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {requests.filter(r => r.status === "pending").length}
                </p>
                <p className="text-xs text-slate-500">قيد الانتظار</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {requests.filter(r => r.status === "scheduled").length}
                </p>
                <p className="text-xs text-slate-500">مجدول</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {requests.filter(r => r.status === "in_progress").length}
                </p>
                <p className="text-xs text-slate-500">قيد التنفيذ</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {requests.filter(r => r.status === "completed").length}
                </p>
                <p className="text-xs text-slate-500">مكتمل</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث في الطلبات..."
                className="pr-10 text-right"
                data-testid="search-requests-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="الحالة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="pending">قيد الانتظار</SelectItem>
                <SelectItem value="scheduled">مجدول</SelectItem>
                <SelectItem value="in_progress">قيد التنفيذ</SelectItem>
                <SelectItem value="completed">مكتمل</SelectItem>
                <SelectItem value="cancelled">ملغي</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="priority-filter">
                <SelectValue placeholder="الأولوية" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأولويات</SelectItem>
                <SelectItem value="urgent">عاجلة</SelectItem>
                <SelectItem value="high">عالية</SelectItem>
                <SelectItem value="medium">متوسطة</SelectItem>
                <SelectItem value="low">منخفضة</SelectItem>
              </SelectContent>
            </Select>
            {user?.role === "admin" && (
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="type-filter">
                  <SelectValue placeholder="النوع" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأنواع</SelectItem>
                  <SelectItem value="structural">إنشائي</SelectItem>
                  <SelectItem value="it">تقنية المعلومات</SelectItem>
                  <SelectItem value="medical">طبي</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="month-filter">
                <SelectValue placeholder="الشهر" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأشهر</SelectItem>
                {monthOptions.map((ym) => (
                  <SelectItem key={ym} value={ym}>{formatMonthLabel(ym)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {filteredRequests.length > 0 ? (
        <div className="grid gap-4">
          {filteredRequests.map((request) => (
            <Card 
              key={request.id} 
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" 
              data-testid={`request-card-${request.id}`}
              onClick={() => openDetailDialog(request)}
            >
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Wrench className="w-5 h-5 text-[#655dc6]" />
                      <h3 className="font-semibold text-lg">{request.clinic_name || request.location_name}</h3>
                      {(request.location_floor || request.location_building) && (
                        <span className="text-sm text-slate-500">
                          ({[request.location_floor, request.location_building].filter(Boolean).join(" - ")})
                        </span>
                      )}
                      <Badge className={`priority-${request.priority} flex items-center gap-1`}>
                        {request.priority === "urgent" && <AlertTriangle className="w-3 h-3" />}
                        {getPriorityLabel(request.priority)}
                      </Badge>
                    </div>
                    <p className="text-slate-600 mb-3 line-clamp-2">{request.description}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {getCategoryLabel(request.category, request)}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {new Date(request.created_at).toLocaleDateString("ar-SA")}
                      </span>
                      <Badge className={getElementTypeColor(request.element_type)}>
                        {getElementTypeLabel(request.element_type)}
                      </Badge>
                      {request.images?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <ImageIcon className="w-4 h-4" />
                          {request.images.length} صورة
                        </span>
                      )}
                    </div>
                    {request.assigned_to && (
                      <div className="mt-2 text-sm">
                        <span className="text-slate-500">المسؤول: </span>
                        <span className="font-medium text-green-600">{request.assigned_to}</span>
                      </div>
                    )}
                    {request.completion_requested && (
                      <div className="mt-1">
                        <Badge className="bg-amber-100 text-amber-700 text-xs">بانتظار تأكيد الإكمال</Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={`status-${request.status} flex items-center gap-1`}>
                      {getStatusIcon(request.status)}
                      {getStatusLabel(request.status)}
                    </Badge>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetailDialog(request)}
                        className="text-[#655dc6] border-[#655dc6] hover:bg-[#F9F7FB]"
                        data-testid={`view-request-${request.id}`}
                      >
                        <Eye className="w-4 h-4 ml-1" />
                        عرض
                      </Button>
                      <a href={`/maintenance/${request.id}/print`} target="_blank" rel="noreferrer">
                        <Button
                          variant="outline"
                          size="sm"
                          title="طباعة النموذج"
                          data-testid={`print-request-${request.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </a>
                      {["admin", "maintenance_manager", "it_manager", "medical_manager"].includes(user?.role) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(request)}
                          data-testid={`edit-request-${request.id}`}
                        >
                          <Pencil className="w-4 h-4 ml-1" />
                          تعديل
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Wrench className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا توجد طلبات صيانة</h3>
            <p className="text-slate-500">
              {searchQuery || statusFilter !== "all" || priorityFilter !== "all" || monthFilter !== "all" || typeFilter !== "all"
                ? "لم يتم العثور على نتائج مطابقة"
                : "لم يتم تقديم أي طلبات صيانة بعد"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">تحديث طلب الصيانة</DialogTitle>
            <DialogDescription>
              {selectedRequest?.clinic_name} - {getCategoryLabel(selectedRequest?.category, selectedRequest)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>الحالة</Label>
              <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                <SelectTrigger data-testid="update-status-select">
                  <SelectValue placeholder="اختر الحالة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">قيد الانتظار</SelectItem>
                  <SelectItem value="scheduled">مجدول</SelectItem>
                  <SelectItem value="in_progress">قيد التنفيذ</SelectItem>
                  <SelectItem value="completed">مكتمل</SelectItem>
                  <SelectItem value="cancelled">ملغي</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>تاريخ الصيانة</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-right" data-testid="date-picker-btn">
                    <CalendarIcon className="ml-2 h-4 w-4" />
                    {updateData.scheduled_date
                      ? format(updateData.scheduled_date, "PPP", { locale: ar })
                      : "اختر التاريخ"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={updateData.scheduled_date}
                    onSelect={(date) => setUpdateData({ ...updateData, scheduled_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>المسؤول عن التنفيذ</Label>
              <Select
                value={updateData.assigned_to_user_id || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setUpdateData({
                      ...updateData,
                      assigned_to: "",
                      assigned_to_user_id: ""
                    });
                  } else {
                    const tech = technicians.find(t => t.id === value);
                    setUpdateData({
                      ...updateData,
                      assigned_to: tech ? tech.full_name : "",
                      assigned_to_user_id: value
                    });
                  }
                }}
              >
                <SelectTrigger data-testid="assigned-to-select">
                  <SelectValue placeholder="اختر الفني المسؤول" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">بدون تعيين</SelectItem>
                  {technicians.length > 0 ? (
                    technicians.map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>
                        {tech.full_name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-techs" disabled>
                      لا يوجد فنيين - أضف فني من صفحة المستخدمين
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>ملاحظات</Label>
              <Textarea
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                placeholder="أضف ملاحظات..."
                className="text-right min-h-[100px]"
                data-testid="notes-textarea"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleUpdate}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={submitting}
                data-testid="submit-update-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "تحديث"}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              تفاصيل طلب الصيانة
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.clinic_name || selectedRequest?.location_name}
              {(selectedRequest?.location_floor || selectedRequest?.location_building) && (
                <span className="mr-2">
                  ({[selectedRequest?.location_floor, selectedRequest?.location_building].filter(Boolean).join(" - ")})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Status and Priority */}
              <div className="flex items-center gap-3">
                <Badge className={`status-${selectedRequest.status} flex items-center gap-1`}>
                  {getStatusIcon(selectedRequest.status)}
                  {getStatusLabel(selectedRequest.status)}
                </Badge>
                <Badge className={`priority-${selectedRequest.priority}`}>
                  {selectedRequest.priority === "urgent" && <AlertTriangle className="w-3 h-3 ml-1" />}
                  {getPriorityLabel(selectedRequest.priority)}
                </Badge>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg col-span-2">
                  <p className="text-sm text-slate-500 mb-1">الموقع</p>
                  <p className="font-medium text-[#655dc6]">
                    {selectedRequest.clinic_name || selectedRequest.location_name}
                    {(selectedRequest.location_floor || selectedRequest.location_building) && (
                      <span className="text-slate-500 font-normal text-sm mr-2">
                        ({[selectedRequest.location_floor, selectedRequest.location_building].filter(Boolean).join(" - ")})
                      </span>
                    )}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">نوع العنصر</p>
                  <p className="font-medium text-[#655dc6]">
                    {getElementTypeLabel(selectedRequest.element_type)}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">الفئة</p>
                  <p className="font-medium text-[#655dc6]">{getCategoryLabel(selectedRequest.category, selectedRequest)}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">تاريخ الإنشاء</p>
                  <p className="font-medium text-[#655dc6]">
                    {new Date(selectedRequest.created_at).toLocaleDateString("ar-SA")}
                  </p>
                </div>
                {selectedRequest.scheduled_date && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-500 mb-1">موعد الصيانة</p>
                    <p className="font-medium text-blue-700">
                      {new Date(selectedRequest.scheduled_date).toLocaleDateString("ar-SA")}
                    </p>
                  </div>
                )}
              </div>

              {/* Warranty Alert for Dental Chairs */}
              {selectedRequest.category === "dental_chair" && selectedRequest.clinic_name && (() => {
                const warranty = getWarrantyInfo(selectedRequest.clinic_name);
                if (!warranty) return null;
                return warranty.isActive ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="warranty-active">
                    <div className="flex items-center gap-2 text-emerald-700 mb-2">
                      <ShieldCheck className="w-5 h-5" />
                      <span className="font-semibold text-sm">كرسي الأسنان تحت الضمان</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500">تاريخ الشراء: </span>
                        <span className="font-medium">{warranty.purchaseDate.toLocaleDateString("ar-SA")}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">انتهاء الضمان: </span>
                        <span className="font-medium">{warranty.expiryDate.toLocaleDateString("ar-SA")}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">مدة الضمان: </span>
                        <span className="font-medium">{warranty.warrantyYears} سنوات</span>
                      </div>
                      <div>
                        <span className="text-slate-500">المتبقي: </span>
                        <span className="font-bold text-emerald-700">{warranty.daysLeft} يوم</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="warranty-expired">
                    <div className="flex items-center gap-2 text-red-700 mb-2">
                      <ShieldAlert className="w-5 h-5" />
                      <span className="font-semibold text-sm">انتهى ضمان كرسي الأسنان</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500">تاريخ الشراء: </span>
                        <span className="font-medium">{warranty.purchaseDate.toLocaleDateString("ar-SA")}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">انتهى بتاريخ: </span>
                        <span className="font-bold text-red-700">{warranty.expiryDate.toLocaleDateString("ar-SA")}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">مدة الضمان: </span>
                        <span className="font-medium">{warranty.warrantyYears} سنوات</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Reporter Info - Hidden for technicians */}
              {!["technician", "maintenance_technician"].includes(user?.role) && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2">معلومات المبلّغ</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="flex items-center gap-2 text-[#655dc6]">
                      <User className="w-4 h-4" />
                      {selectedRequest.created_by_name}
                    </span>
                    {selectedRequest.reporter_whatsapp && (
                      <span className="flex items-center gap-2 text-green-600">
                        <MessageCircle className="w-4 h-4" />
                        <span dir="ltr">{selectedRequest.reporter_whatsapp}</span>
                      </span>
                    )}
                    {selectedRequest.reporter_phone && !selectedRequest.reporter_whatsapp && (
                      <span className="flex items-center gap-2 text-[#655dc6]">
                        <Phone className="w-4 h-4" />
                        {selectedRequest.reporter_phone}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-sm text-slate-500 mb-2">الوصف</p>
                <p className="p-4 bg-slate-50 rounded-lg text-slate-700">{selectedRequest.description}</p>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">ملاحظات الإدارة</p>
                  <p className="p-4 bg-amber-50 rounded-lg text-amber-800">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Assigned To */}
              {selectedRequest.assigned_to && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">المسؤول عن التنفيذ</p>
                  <p className="font-medium text-green-800">{selectedRequest.assigned_to}</p>
                </div>
              )}

              {/* Expense / Purchases Section */}
              {selectedRequest.status !== "completed" && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-[#655dc6] mb-3 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    المشتريات والمصاريف
                  </h4>

                  {/* No expense yet */}
                  {(!expenseData || !expenseData.has_purchases) && (
                    <>
                      {!showExpenseForm && !showDirectExpenseForm ? (
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setShowExpenseForm(true)}
                            className="w-full border-dashed border-[#655dc6] text-[#655dc6] hover:bg-[#F9F7FB]"
                            data-testid="add-expense-btn"
                          >
                            <CircleDollarSign className="w-4 h-4 ml-2" />
                            هل هناك مشتريات لهذا الطلب؟
                          </Button>
                          {["admin", "maintenance_manager", "it_manager", "medical_manager"].includes(user?.role) && (
                            <Button
                              variant="outline"
                              onClick={() => setShowDirectExpenseForm(true)}
                              className="w-full border-dashed border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                              data-testid="add-direct-expense-btn"
                            >
                              <Receipt className="w-4 h-4 ml-2" />
                              إضافة فاتورة مشتريات مباشرة
                            </Button>
                          )}
                        </div>
                      ) : showDirectExpenseForm ? (
                        <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid="direct-expense-form">
                          <div className="space-y-2">
                            <Label className="text-sm">تشخيص العطل *</Label>
                            <Textarea
                              value={expenseDiagnosis}
                              onChange={(e) => setExpenseDiagnosis(e.target.value)}
                              placeholder="وصف العطل والمواد المطلوبة..."
                              className="text-right min-h-[60px]"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label className="text-sm">التكلفة التقديرية (ريال) *</Label>
                              <Input type="number" value={expenseEstimatedCost} onChange={(e) => setExpenseEstimatedCost(e.target.value)} placeholder="0.00" className="text-right" dir="ltr" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">المبلغ الفعلي (ريال) *</Label>
                              <Input type="number" value={expenseActualAmount} onChange={(e) => setExpenseActualAmount(e.target.value)} placeholder="0.00" className="text-right" dir="ltr" />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">صورة الفاتورة *</Label>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg cursor-pointer hover:bg-slate-50 text-sm text-slate-600">
                                {uploadingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                {uploadingInvoice ? "جاري الرفع..." : "رفع صورة الفاتورة"}
                                <input type="file" accept="image/*" className="hidden" onChange={handleInvoiceImageUpload} disabled={uploadingInvoice} />
                              </label>
                              {expenseInvoiceImage && (
                                <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-emerald-400">
                                  <img src={expenseInvoiceImage} alt="فاتورة" className="w-full h-full object-cover" />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={submitDirectExpense} disabled={submittingExpense} className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white">
                              {submittingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ المشتريات والفاتورة"}
                            </Button>
                            <Button variant="outline" onClick={() => setShowDirectExpenseForm(false)} className="flex-1">إلغاء</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid="expense-diagnosis-form">
                          <div className="space-y-2">
                            <Label className="text-sm">تشخيص العطل *</Label>
                            <Textarea
                              value={expenseDiagnosis}
                              onChange={(e) => setExpenseDiagnosis(e.target.value)}
                              placeholder="وصف العطل والمواد المطلوبة..."
                              className="text-right min-h-[80px]"
                              data-testid="expense-diagnosis-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">التكلفة التقديرية (ريال) *</Label>
                            <Input
                              type="number"
                              value={expenseEstimatedCost}
                              onChange={(e) => setExpenseEstimatedCost(e.target.value)}
                              placeholder="0.00"
                              className="text-right"
                              dir="ltr"
                              data-testid="expense-cost-input"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={submitDiagnosis}
                              disabled={submittingExpense}
                              className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                              data-testid="submit-diagnosis-btn"
                            >
                              {submittingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : "إرسال للموافقة"}
                            </Button>
                            <Button variant="outline" onClick={() => setShowExpenseForm(false)} className="flex-1">
                              إلغاء
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Expense pending approval - show to managers */}
                  {expenseData?.status === "pending_approval" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="expense-pending-approval">
                      <div className="flex items-center gap-2 text-amber-700">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium text-sm">بانتظار موافقة المدير</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-slate-500">التشخيص:</span> {expenseData.diagnosis}</p>
                        <p><span className="text-slate-500">التكلفة التقديرية:</span> <span className="font-bold text-[#655dc6]">{expenseData.estimated_cost} ريال</span></p>
                        <p><span className="text-slate-500">مقدم من:</span> {expenseData.submitted_by_name}</p>
                      </div>
                      {["admin", "maintenance_manager", "it_manager", "medical_manager"].includes(user?.role) && (
                        <div className="space-y-2 pt-2 border-t border-amber-200">
                          <Textarea
                            value={expenseApprovalNotes}
                            onChange={(e) => setExpenseApprovalNotes(e.target.value)}
                            placeholder="ملاحظات (اختياري)..."
                            className="text-right min-h-[60px] text-sm"
                            data-testid="expense-approval-notes"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleExpenseApproval(true)}
                              disabled={submittingExpense}
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid="approve-expense-btn"
                            >
                              {submittingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileCheck className="w-4 h-4 ml-1" />موافقة</>}
                            </Button>
                            <Button
                              onClick={() => handleExpenseApproval(false)}
                              disabled={submittingExpense}
                              variant="outline"
                              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                              data-testid="reject-expense-btn"
                            >
                              رفض
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expense approved - technician uploads invoice */}
                  {expenseData?.status === "approved" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="expense-approved">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="font-medium text-sm">تمت الموافقة — بانتظار رفع الفاتورة</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-slate-500">التشخيص:</span> {expenseData.diagnosis}</p>
                        <p><span className="text-slate-500">التكلفة التقديرية:</span> <span className="font-bold">{expenseData.estimated_cost} ريال</span></p>
                        {expenseData.approval?.notes && <p><span className="text-slate-500">ملاحظات المدير:</span> {expenseData.approval.notes}</p>}
                      </div>
                      <div className="space-y-2 pt-2 border-t border-emerald-200">
                        <Label className="text-sm">صورة الفاتورة *</Label>
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg cursor-pointer hover:bg-slate-50 text-sm text-slate-600">
                            {uploadingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {uploadingInvoice ? "جاري الرفع..." : "رفع صورة الفاتورة"}
                            <input type="file" accept="image/*" className="hidden" onChange={handleInvoiceImageUpload} disabled={uploadingInvoice} />
                          </label>
                          {expenseInvoiceImage && (
                            <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-emerald-400">
                              <img src={expenseInvoiceImage} alt="فاتورة" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">المبلغ الفعلي (ريال) *</Label>
                          <Input
                            type="number"
                            value={expenseActualAmount}
                            onChange={(e) => setExpenseActualAmount(e.target.value)}
                            placeholder="0.00"
                            className="text-right"
                            dir="ltr"
                            data-testid="expense-actual-amount"
                          />
                        </div>
                        <Button
                          onClick={submitInvoice}
                          disabled={submittingExpense || !expenseInvoiceImage || !expenseActualAmount}
                          className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white"
                          data-testid="submit-invoice-btn"
                        >
                          {submittingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Receipt className="w-4 h-4 ml-2" />رفع الفاتورة</>}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Expense rejected */}
                  {expenseData?.status === "rejected" && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="expense-rejected">
                      <div className="flex items-center gap-2 text-red-700 mb-2">
                        <X className="w-4 h-4" />
                        <span className="font-medium text-sm">تم رفض طلب المشتريات</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-slate-500">التشخيص:</span> {expenseData.diagnosis}</p>
                        <p><span className="text-slate-500">التكلفة التقديرية:</span> {expenseData.estimated_cost} ريال</p>
                        {expenseData.approval?.notes && <p><span className="text-slate-500">سبب الرفض:</span> {expenseData.approval.notes}</p>}
                      </div>
                    </div>
                  )}

                  {/* Invoice uploaded - show summary */}
                  {expenseData?.status === "invoice_uploaded" && (
                    <div className="bg-[#F9F7FB] border border-[#655dc6]/20 rounded-lg p-4" data-testid="expense-complete">
                      <div className="flex items-center gap-2 text-[#655dc6] mb-3">
                        <FileCheck className="w-4 h-4" />
                        <span className="font-medium text-sm">تم رفع الفاتورة</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">التكلفة التقديرية</p>
                          <p className="font-bold">{expenseData.estimated_cost} ريال</p>
                        </div>
                        <div>
                          <p className="text-slate-500">المبلغ الفعلي</p>
                          <p className="font-bold text-[#655dc6]">{expenseData.invoice?.actual_amount} ريال</p>
                        </div>
                      </div>
                      <p className="text-sm mt-2"><span className="text-slate-500">التشخيص:</span> {expenseData.diagnosis}</p>
                      {expenseData.invoice?.image && (
                        <div className="mt-3">
                          <p className="text-sm text-slate-500 mb-1">صورة الفاتورة:</p>
                          <a href={expenseData.invoice.image} target="_blank" rel="noopener noreferrer" className="block w-32 h-32 rounded-lg overflow-hidden border hover:shadow-md transition-shadow">
                            <img src={expenseData.invoice.image} alt="فاتورة" className="w-full h-full object-cover" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Show expense summary even when completed */}
              {selectedRequest.status === "completed" && expenseData?.has_purchases && (
                <div className="bg-[#F9F7FB] border border-[#655dc6]/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-[#655dc6] mb-3">
                    <Receipt className="w-4 h-4" />
                    <span className="font-medium text-sm">ملخص المشتريات</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">التكلفة التقديرية</p>
                      <p className="font-bold">{expenseData.estimated_cost} ريال</p>
                    </div>
                    {expenseData.invoice && (
                      <div>
                        <p className="text-slate-500">المبلغ الفعلي</p>
                        <p className="font-bold text-[#655dc6]">{expenseData.invoice.actual_amount} ريال</p>
                      </div>
                    )}
                  </div>
                  <p className="text-sm mt-2"><span className="text-slate-500">التشخيص:</span> {expenseData.diagnosis}</p>
                  {expenseData.invoice?.image && (
                    <div className="mt-3">
                      <p className="text-sm text-slate-500 mb-1">صورة الفاتورة:</p>
                      <a href={expenseData.invoice.image} target="_blank" rel="noopener noreferrer" className="block w-32 h-32 rounded-lg overflow-hidden border hover:shadow-md transition-shadow">
                        <img src={expenseData.invoice.image} alt="فاتورة" className="w-full h-full object-cover" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Images */}
              {selectedRequest.images?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    الصور المرفقة ({selectedRequest.images.length})
                  </p>
                  <div className="relative">
                    <div className="rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={selectedRequest.images[currentImageIndex]}
                        alt={`صورة ${currentImageIndex + 1}`}
                        className="w-full h-64 object-contain"
                        data-testid="request-image"
                      />
                    </div>
                    {selectedRequest.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex((prev) => 
                            prev === 0 ? selectedRequest.images.length - 1 : prev - 1
                          )}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg"
                          data-testid="prev-image-btn"
                        >
                          <ChevronRight className="w-5 h-5 text-[#655dc6]" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex((prev) => 
                            prev === selectedRequest.images.length - 1 ? 0 : prev + 1
                          )}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg"
                          data-testid="next-image-btn"
                        >
                          <ChevronLeft className="w-5 h-5 text-[#655dc6]" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                          {selectedRequest.images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === currentImageIndex ? "bg-[#655dc6]" : "bg-white/60"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Thumbnails */}
                  {selectedRequest.images.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                      {selectedRequest.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                            idx === currentImageIndex ? "border-[#655dc6]" : "border-transparent"
                          }`}
                        >
                          <img src={img} alt={`صورة ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline / Activity Log */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-[#4a3fa8] mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  السجل الزمني
                </h4>
                
                {/* Add Entry Form */}
                {selectedRequest.status !== "completed" && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 space-y-3" data-testid="timeline-form">
                    <div className="flex gap-2">
                      {[
                        { value: "comment", label: "تعليق" },
                        { value: "before_photo", label: "صور قبل" },
                        { value: "after_photo", label: "صور بعد" }
                      ].map(t => (
                        <button
                          key={t.value}
                          onClick={() => setTimelineType(t.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                            timelineType === t.value
                              ? "bg-[#4a3fa8] text-white"
                              : "bg-white text-slate-600 border hover:bg-slate-100"
                          }`}
                        >{t.label}</button>
                      ))}
                    </div>
                    <textarea
                      value={timelineComment}
                      onChange={(e) => setTimelineComment(e.target.value)}
                      placeholder="أضف تعليقاً أو ملاحظة..."
                      className="w-full p-2 border rounded-lg text-sm resize-none h-16 focus:ring-1 focus:ring-[#655dc6] focus:border-[#655dc6]"
                      data-testid="timeline-comment-input"
                    />
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 px-3 py-1.5 bg-white border rounded-lg cursor-pointer hover:bg-slate-50 text-xs text-slate-600">
                        {uploadingTimelineImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                        {uploadingTimelineImg ? "جاري الرفع..." : "إرفاق صورة"}
                        <input type="file" accept="image/*" className="hidden" onChange={handleTimelineImageUpload} disabled={uploadingTimelineImg} />
                      </label>
                      {timelineImages.length > 0 && (
                        <div className="flex gap-1">
                          {timelineImages.map((img, i) => (
                            <div key={i} className="relative w-10 h-10 rounded overflow-hidden border">
                              <img src={img} alt="" className="w-full h-full object-cover" />
                              <button
                                onClick={() => setTimelineImages(prev => prev.filter((_, idx) => idx !== i))}
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                              >x</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button
                        onClick={submitTimelineEntry}
                        disabled={submittingTimeline || (!timelineComment && timelineImages.length === 0)}
                        size="sm"
                        className="bg-[#4a3fa8] hover:bg-[#3d3291] text-white mr-auto"
                        data-testid="submit-timeline-btn"
                      >
                        {submittingTimeline ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 ml-1" />}
                        إرسال
                      </Button>
                    </div>
                  </div>
                )}

                {/* Timeline Entries */}
                <div className="space-y-3 max-h-60 overflow-y-auto" data-testid="timeline-list">
                  {(selectedRequest.timeline || []).slice().reverse().map((entry, i) => {
                    const actionColors = {
                      created: "border-green-300 bg-green-50",
                      status_changed: "border-blue-300 bg-blue-50",
                      assigned: "border-purple-300 bg-purple-50",
                      scheduled: "border-indigo-300 bg-indigo-50",
                      comment: "border-slate-300 bg-white",
                      before_photo: "border-yellow-300 bg-yellow-50",
                      after_photo: "border-emerald-300 bg-emerald-50",
                      expense_diagnosis: "border-orange-300 bg-orange-50",
                      expense_approval: "border-teal-300 bg-teal-50",
                      expense_invoice: "border-[#655dc6]/40 bg-[#F9F7FB]",
                      completion_requested: "border-amber-300 bg-amber-50",
                      completion_rejected: "border-red-300 bg-red-50",
                      redirected: "border-indigo-300 bg-indigo-50"
                    };
                    const actionLabels = {
                      created: "إنشاء",
                      status_changed: "تغيير حالة",
                      assigned: "تعيين",
                      scheduled: "جدولة",
                      comment: "تعليق",
                      before_photo: "صور قبل",
                      after_photo: "صور بعد",
                      expense_diagnosis: "تشخيص ومشتريات",
                      expense_approval: "موافقة مشتريات",
                      expense_invoice: "فاتورة مشتريات",
                      completion_requested: "طلب إكمال",
                      completion_rejected: "رفض إكمال",
                      redirected: "تحويل طلب"
                    };
                    const colorClass = actionColors[entry.action] || "border-slate-300 bg-white";
                    return (
                      <div key={i} className={`border-r-4 rounded-lg p-3 ${colorClass}`} data-testid={`timeline-entry-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{actionLabels[entry.action] || entry.label}</Badge>
                            <span className="text-xs text-slate-500">{entry.user}</span>
                          </div>
                          <span className="text-xs text-slate-400" dir="ltr">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString("ar-SA") + " " + new Date(entry.timestamp).toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" }) : ""}
                          </span>
                        </div>
                        {entry.details && <p className="text-sm text-slate-700">{entry.details}</p>}
                        {entry.images?.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {entry.images.map((img, j) => (
                              <a key={j} href={img} target="_blank" rel="noopener noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden border hover:shadow-md transition-shadow">
                                <img src={img} alt="" className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(!selectedRequest.timeline || selectedRequest.timeline.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-3">لا يوجد سجل زمني بعد</p>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              {selectedRequest.status !== "completed" && (
                <div className="pt-4 border-t space-y-3">
                  {/* Completion request banner - visible to managers */}
                  {selectedRequest.completion_requested && ["admin", "maintenance_manager", "it_manager", "medical_manager"].includes(user?.role) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="completion-request-banner">
                      <div className="flex items-center gap-2 text-amber-700 mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium text-sm">طلب إكمال من: {selectedRequest.completion_requested_by_name}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApproveCompletion(selectedRequest.id, true)}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="approve-completion-btn"
                        >
                          <CheckCircle2 className="w-4 h-4 ml-2" />
                          تأكيد الإكمال
                        </Button>
                        <Button
                          onClick={() => handleApproveCompletion(selectedRequest.id, false)}
                          variant="outline"
                          className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                          data-testid="reject-completion-btn"
                        >
                          رفض
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Technician actions */}
                  {["technician", "maintenance_technician"].includes(user?.role) && (
                    <div className="flex gap-3">
                      {!selectedRequest.completion_requested ? (
                        <Button
                          onClick={requestCompletion}
                          disabled={requestingCompletion}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="request-completion-btn"
                        >
                          {requestingCompletion ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 ml-2" />طلب إكمال</>}
                        </Button>
                      ) : (
                        <div className="flex-1 p-3 bg-amber-50 rounded-lg text-center text-amber-700 text-sm font-medium">
                          <Clock className="w-4 h-4 inline ml-1" />
                          تم إرسال طلب الإكمال — بانتظار موافقة المدير
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manager actions */}
                  {["admin", "maintenance_manager", "it_manager", "medical_manager"].includes(user?.role) && (
                    <div className="flex gap-3">
                      {selectedRequest.status === "pending" && (
                        <Button
                          onClick={() => handleQuickStatusUpdate(selectedRequest.id, "in_progress")}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="start-work-btn"
                        >
                          <Wrench className="w-4 h-4 ml-2" />
                          بدء العمل
                        </Button>
                      )}
                      <Button
                        onClick={() => handleQuickStatusUpdate(selectedRequest.id, "completed")}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="mark-complete-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 ml-2" />
                        إكمال الطلب
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setDetailDialogOpen(false);
                          openEditDialog(selectedRequest);
                        }}
                        data-testid="edit-from-detail-btn"
                      >
                        <Pencil className="w-4 h-4 ml-2" />
                        تعديل
                      </Button>
                    </div>
                  )}

                  {/* Redirect to another manager */}
                  {["admin", "maintenance_manager", "it_manager", "medical_manager"].includes(user?.role) && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm text-slate-500 flex items-center gap-1 w-full">
                        <ArrowLeftRight className="w-4 h-4" /> تحويل الطلب إلى:
                      </span>
                      {user?.role !== "maintenance_manager" && (
                        <Button size="sm" variant="outline" disabled={redirecting}
                          onClick={() => handleRedirectRequest(selectedRequest.id, "maintenance_manager")}
                          className="text-xs border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="redirect-maintenance-btn">
                          مدير الصيانة
                        </Button>
                      )}
                      {user?.role !== "it_manager" && (
                        <Button size="sm" variant="outline" disabled={redirecting}
                          onClick={() => handleRedirectRequest(selectedRequest.id, "it_manager")}
                          className="text-xs border-teal-300 text-teal-600 hover:bg-teal-50" data-testid="redirect-it-btn">
                          مدير تقنية المعلومات
                        </Button>
                      )}
                      {user?.role !== "medical_manager" && (
                        <Button size="sm" variant="outline" disabled={redirecting}
                          onClick={() => handleRedirectRequest(selectedRequest.id, "medical_manager")}
                          className="text-xs border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="redirect-medical-btn">
                          المدير الطبي
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedRequest.status === "completed" && (
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-emerald-700 font-medium">تم إكمال هذا الطلب</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceRequests;
